"use client";

import React, { FC, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "@/app/lib/hooks";
import { useReviewsAggregate } from "../../(home)/desktop/useReviewsAggregate";
import SectionNewsletter from "../../(home)/desktop/SectionNewsletter";

interface Continent {
  code: string;
  name: string;
  slug?: string;
  nameTranslated?: string;
  image?: string;
  content_cms_entry__?: string;
}

const CONTINENT_EMOJI: Record<string, string> = {
  EU: "🌐",
  AS: "🌏",
  NA: "🌎",
  SA: "🌎",
  AF: "🌍",
  OC: "🏝️",
  AN: "❄️",
};

interface Country {
  code: string;
  name: string;
  slug: string;
  continentId: string;
  numCities?: number;
  numTours?: number;
  nameTranslated?: string;
  mainImage?: string | null;
  createdAt?: string;
}

interface FeaturedCity {
  id: number;
  slug: string;
  name: string;
  nameTranslated?: string;
  countryId: string;
  totalTours?: number;
  // Some endpoints (e.g. /api/featured-continent-cities) return the count
  // as `tourCount`; the destinations carousel handles both — keep parity.
  tourCount?: number;
  mainImage?: string | null;
  entry?: { data?: Array<{ title?: string; mainImage?: string | null }> };
}

export interface ContinentPageInitial {
  continent: Continent | null;
  countries: Country[];
  curatedCities: FeaturedCity[];
  curatedCountries: Array<{ id: number; badge: string | null; country: Country }>;
  cmsTitle: string | null;
  cmsHeroImage: string | null;
  cmsTagline: string | null;
}

interface Props {
  initial: ContinentPageInitial;
}

/**
 * Public /continent/[slug] — 2026 redesign. Now hydrates from a Server
 * Component that pre-fetches everything (see `page.tsx` next to this
 * file), so first paint already contains the hero text, image and trio.
 *
 * The client component itself stays interactive (hover states, etc.)
 * but never refires the initial fetches — they're done.
 */
const ContinentPage: FC<Props> = ({ initial }) => {
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const reviewsAggregate = useReviewsAggregate();

  // Initial state hydrated from the Server Component. No client-side
  // refetch — locale-derived bits like number formatting still react to
  // the Redux `selectedLanguage` because the server resolved the same
  // value from the URL `[lang]` segment.
  const continent = initial.continent;
  const countries = initial.countries;
  const curatedCities = initial.curatedCities;
  const curatedCountries = initial.curatedCountries;
  const cmsTitle = initial.cmsTitle;
  const cmsHeroImage = initial.cmsHeroImage;
  const cmsTagline = initial.cmsTagline;

  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage
  ) || (lang ? String(lang) : "fr_FR");

  const totals = useMemo(() => {
    const villes = countries.reduce((a, c) => a + (c.numCities || 0), 0);
    const tours = countries.reduce((a, c) => a + (c.numTours || 0), 0);
    return { pays: countries.length, villes, tours };
  }, [countries]);

  // Sort by numTours descending — drives the "À l'honneur" trio + badge
  // assignment. Stable, real-data-driven, refreshes on its own as numTours
  // changes in the admin.
  const sortedCountries = useMemo(
    () =>
      [...countries].sort(
        (a, b) => (b.numTours || 0) - (a.numTours || 0)
      ),
    [countries]
  );

  // Index the fully-hydrated countries (with mainImage, numTours,
  // nameTranslated, …) by their code so we can merge the slim curated
  // payload with the rich data the public page already has on hand.
  const countriesByCode = useMemo(() => {
    const map = new Map<string, Country>();
    for (const c of countries) if (c.code) map.set(c.code, c);
    return map;
  }, [countries]);

  // Admin-curated top-3 wins when set; otherwise fall back to the
  // numTours-derived ranking so un-curated continents still show 3 cards.
  // Each entry carries an explicit `badge` (enum or null) which the front
  // maps to a localized label below. We require the rich country to be
  // present (image + meta) before surfacing the card.
  const topThree: Array<{ country: Country; badge: string | null }> =
    curatedCountries.length > 0
      ? curatedCountries
          .slice(0, 3)
          .map((row) => {
            const rich = countriesByCode.get(row.country.code);
            return rich ? { country: rich, badge: row.badge } : null;
          })
          .filter(
            (x): x is { country: Country; badge: string | null } => x !== null,
          )
      : sortedCountries.slice(0, 3).map((country, idx) => ({
          country,
          badge:
            (country.numTours || 0) > 0
              ? idx === 0
                ? "most_booked"
                : idx === 1
                  ? "editor_choice"
                  : "trending"
              : null,
        }));
  const allOthers = sortedCountries;
  // Locale-aware label preference: CMS title (admin-managed per language) →
  // raw nameTranslated → name. Always capitalised because the CMS row often
  // arrives lowercased ("europe", "asie").
  const continentLabel =
    capitalize(cmsTitle || continent?.nameTranslated || continent?.name || "") ||
    "";

  const localeFormatter = useMemo(() => {
    const tag = selectedLanguage.replace("_", "-");
    return new Intl.NumberFormat(tag);
  }, [selectedLanguage]);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Friendly fallback while data is in flight — keeps the layout from
  // jumping when stats arrive.
  if (!continent) {
    return (
      <main className="gdx" style={{ background: "var(--g-cream)", minHeight: "60vh" }} />
    );
  }

  return (
    <>
      <main className="gdx" style={{ background: "var(--g-cream)" }}>
        {/* MOBILE BREADCRUMB — outside hero, on cream (per 23-mobile-countries.jsx). */}
        {isMobile && (
          <nav
            aria-label="Breadcrumb"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              fontSize: 13,
              color: "var(--g-ink-500)",
              padding: "12px 18px",
            }}
          >
            <Link
              href={`/${lang}/${currency}` as any}
              style={{ color: "var(--g-ink-500)", textDecoration: "none" }}
            >
              {t("breadcrumb_home", { defaultValue: "Accueil" })}
            </Link>
            <span>›</span>
            <Link
              href={`/${lang}/${currency}/continents` as any}
              style={{ color: "var(--g-ink-500)", textDecoration: "none" }}
            >
              {t("continents_breadcrumb", { defaultValue: "Continents" })}
            </Link>
            <span>›</span>
            <span style={{ color: "var(--g-burgundy)", fontWeight: 700 }}>
              {continentLabel}
            </span>
          </nav>
        )}

        {/* HERO ------------------------------------------------------- */}
        <section
          style={{
            position: "relative",
            // Mobile: shorter image hero per 22-mobile-home.jsx feel. Desktop:
            // fluid clamp 320–480 px per 07-desktop-europe.jsx hero.
            height: isMobile ? "auto" : "clamp(320px, 38vw, 480px)",
            minHeight: isMobile ? 0 : 320,
            backgroundImage: (cmsHeroImage || continent.image)
              ? `url("${encodeURI(cmsHeroImage || (continent.image as string))}")`
              : undefined,
            backgroundColor: "#4a6f5a",
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: "white",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: isMobile
                ? "linear-gradient(135deg, rgba(91,31,58,.88) 0%, rgba(91,31,58,.55) 55%, rgba(91,31,58,.25) 100%)"
                : "linear-gradient(90deg, rgba(20,15,30,.85) 0%, rgba(20,15,30,.55) 60%, rgba(20,15,30,.25) 100%)",
            }}
          />
          <div
            style={{
              position: "relative",
              maxWidth: 1280,
              margin: "0 auto",
              padding: isMobile ? "20px 18px 28px" : "32px 56px 48px",
              height: isMobile ? "auto" : "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: isMobile ? "flex-start" : "space-between",
              gap: isMobile ? 16 : 0,
            }}
          >
            {/* Breadcrumb inside hero — desktop only (mobile shows it on
                cream above the hero). */}
            {!isMobile && (
              <nav
                aria-label="Breadcrumb"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 13,
                  color: "rgba(255,255,255,.75)",
                }}
              >
                <Link
                  href={`/${lang}/${currency}` as any}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {t("breadcrumb_home", { defaultValue: "Accueil" })}
                </Link>
                <span>›</span>
                <span>{t("nav_destinations", { defaultValue: "Destinations" })}</span>
                <span>›</span>
                <Link
                  href={`/${lang}/${currency}/continents` as any}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {t("continents_breadcrumb", { defaultValue: "Continents" })}
                </Link>
                <span>›</span>
                <span style={{ color: "white", fontWeight: 600 }}>{continentLabel}</span>
              </nav>
            )}

            <div>
              {isMobile ? (
                <div
                  style={{
                    fontSize: 22,
                    color: "var(--g-coral-soft)",
                    fontWeight: 900,
                    letterSpacing: "0.06em",
                    marginBottom: 8,
                  }}
                >
                  {continentLabel.toUpperCase()}
                </div>
              ) : (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "5px 12px",
                    borderRadius: 999,
                    background: "rgba(255,255,255,.18)",
                    border: "1px solid rgba(255,255,255,.3)",
                    fontSize: 11,
                    fontWeight: 700,
                    letterSpacing: "0.06em",
                    textTransform: "uppercase",
                    marginBottom: 16,
                    backdropFilter: "blur(6px)",
                  }}
                >
                  <span style={{ fontSize: 14 }} aria-hidden>
                    {CONTINENT_EMOJI[(continent.code || "").toUpperCase()] || "🌐"}
                  </span>
                  <span>{t("continent_page_pill", { defaultValue: "Continent" })}</span>
                  <span style={{ opacity: 0.7 }}>·</span>
                  <span>{continentLabel}</span>
                </span>
              )}
              {/* H1 — desktop only. On mobile the eyebrow above already labels
                  the page and the CMS doesn't currently provide a creative
                  headline distinct from the continent name (rendering it would
                  duplicate the eyebrow, e.g. "EUROPE" + "europe"). */}
              {!isMobile && (
                <h1
                  style={{
                    fontFamily: "var(--g-font-display)",
                    fontSize: "clamp(32px, 5.5vw, 56px)",
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: "-0.02em",
                    margin: "0 0 16px",
                  }}
                >
                  {t("continent_page_title_pre", {
                    continent: continentLabel,
                    defaultValue: "{{continent}} avec des",
                  })}{" "}
                  <span
                    style={{
                      color: "#F8E0E5",
                      fontStyle: "italic",
                      fontWeight: 600,
                    }}
                  >
                    {t("continent_page_title_em", {
                      defaultValue: "guides locaux",
                    })}
                  </span>
                </h1>
              )}
              {/* Hero tagline — only rendered when the marketing team has
                  set ShortContent on the continent's CMS row. No generic
                  fallback: the stats row below already surfaces the same
                  numbers, and an empty CMS tagline = the section should
                  just stay quiet. */}
              {cmsTagline && (
                <p
                  style={{
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,.92)",
                    marginBottom: 28,
                  }}
                >
                  {cmsTagline}
                </p>
              )}
              {/* Stats row — desktop only; mobile design (23-mobile-countries.jsx)
                  keeps the hero compact and surfaces counts inside each card. */}
              {!isMobile && (
                <div
                  style={{
                    display: "flex",
                    gap: 36,
                    flexWrap: "wrap",
                  }}
                >
                  <Stat
                    value={localeFormatter.format(totals.pays)}
                    label={t("continent_page_stat_countries", { defaultValue: "pays" })}
                  />
                  <Stat
                    value={localeFormatter.format(totals.villes)}
                    label={t("continent_page_stat_cities", { defaultValue: "villes" })}
                  />
                  <Stat
                    value={localeFormatter.format(totals.tours)}
                    label={t("continent_page_stat_tours", { defaultValue: "tours" })}
                  />
                  {reviewsAggregate && reviewsAggregate.totalReviews > 0 && (
                    <Stat
                      value={`${reviewsAggregate.avgRating.toLocaleString(
                        selectedLanguage.replace("_", "-"),
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}★`}
                      label={t("continent_page_stat_rating", {
                        defaultValue: "note moyenne",
                      })}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Single content container — all the in-page sections share this
            wrapper so they line up identically (matches the /continents
            layout). The hero above stays full-bleed on purpose.
            Horizontal padding uses the SAME clamp() as SectionNewsletter
            so the trio of (content / newsletter / content) keeps the same
            left/right margins at every viewport width — no visual shift
            on narrow viewports. */}
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            padding: isMobile ? "0 14px" : "0 clamp(20px, 4vw, 56px)",
          }}
        >

        {/* TRUST BAR — desktop only (the mobile design omits it). */}
        {!isMobile && (
        <div style={{ paddingTop: 16 }}>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              alignItems: "center",
              padding: "12px 24px",
              background: "white",
              borderRadius: 12,
              border: "1px solid var(--g-cream-deep)",
              fontSize: 13,
              color: "var(--g-ink-700)",
              marginTop: -28,
              position: "relative",
              zIndex: 2,
              boxShadow: "0 8px 24px -16px rgba(0,0,0,.2)",
            }}
          >
            {reviewsAggregate && reviewsAggregate.totalReviews > 0 && (
              <TrustItem
                icon="★"
                accent
                label={
                  <>
                    <b>
                      {reviewsAggregate.avgRating.toLocaleString(
                        selectedLanguage.replace("_", "-"),
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                      )}
                      /5
                    </b>{" "}
                    ·{" "}
                    {t("continent_page_trust_reviews", {
                      count: reviewsAggregate.totalReviews,
                      defaultValue: `${localeFormatter.format(
                        reviewsAggregate.totalReviews
                      )} avis vérifiés`,
                    })}
                  </>
                }
              />
            )}
            <TrustItem
              icon="✓"
              label={t("continent_page_trust_cancel", {
                defaultValue: "Annulation gratuite jusqu'à 24h avant",
              })}
            />
            <TrustItem
              icon="✓"
              label={t("continent_page_trust_guides", {
                defaultValue: "Guides locaux francophones uniquement",
              })}
            />
            <TrustItem
              icon="✓"
              label={t("continent_page_trust_payment", {
                defaultValue: "Paiement sécurisé Stripe",
              })}
            />
          </div>
        </div>
        )}

        {/* TOP 3 — desktop only (mobile design jumps straight to the
            "Tous les pays" grid). */}
        {!isMobile && topThree.length > 0 && (
          <section style={{ padding: isMobile ? "32px 0 16px" : "64px 0 24px" }}>
            <div style={{ marginBottom: 24 }}>
              <span className="gdx-eyebrow" style={{ color: "var(--g-coral)" }}>
                {t("continent_page_top3_eyebrow", { defaultValue: "À L'HONNEUR" })}
              </span>
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: 800,
                  color: "var(--g-burgundy)",
                  marginTop: 6,
                }}
              >
                {t("continent_page_top3_title", {
                  defaultValue: "Les 3 pays incontournables",
                })}
              </h2>
            </div>

            <div
              className="gdx-top3-grid"
              style={{
                display: "grid",
                // Mobile (< 720px): auto-fit collapses to 1-2 cards per row.
                // Desktop: a CSS rule in globals.css promotes the layout to
                // `2fr 1fr 1fr` so the lead card stays hero-sized.
                gridTemplateColumns:
                  "repeat(auto-fit, minmax(min(260px, 100%), 1fr))",
                gap: "clamp(14px, 2vw, 20px)",
              }}
            >
              {topThree.map(({ country, badge }) => (
                <CountryHeroCard
                  key={country.code}
                  country={country}
                  badge={badge}
                  href={`/${lang}/${currency}/country/${country.slug}`}
                  t={t}
                  localeFormatter={localeFormatter}
                />
              ))}
            </div>
          </section>
        )}

        {/* ALL COUNTRIES GRID --------------------------------------- */}
        {allOthers.length > 0 && (
          <section style={{ padding: isMobile ? "18px 0 16px" : "40px 0 24px" }}>
            <div style={{ marginBottom: isMobile ? 10 : 24 }}>
              {!isMobile && (
                <span className="gdx-eyebrow" style={{ color: "var(--g-coral)" }}>
                  {t("continent_page_all_eyebrow", { defaultValue: "TOUS LES PAYS" })}
                </span>
              )}
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: isMobile ? 15 : 32,
                  fontWeight: 800,
                  color: "var(--g-burgundy)",
                  marginTop: isMobile ? 0 : 6,
                }}
              >
                {isMobile
                  ? t("continent_page_all_title_mobile", {
                      defaultValue: "Tous les pays",
                    })
                  : t("continent_page_all_title", {
                      count: allOthers.length,
                      continent: continentLabel,
                      defaultValue: `${allOthers.length} pays en ${continentLabel}`,
                    })}
              </h2>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr 1fr"
                  : "repeat(auto-fill, minmax(240px, 1fr))",
                gap: isMobile ? 12 : 16,
              }}
            >
              {allOthers.map((country) => {
                const curatedRow = curatedCountries.find(
                  (r) => r.country.code === country.code,
                );
                return (
                  <CountryTile
                    key={country.code}
                    country={country}
                    href={`/${lang}/${currency}/country/${country.slug}`}
                    t={t}
                    localeFormatter={localeFormatter}
                    isMobile={isMobile}
                    badge={curatedRow?.badge || null}
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* VILLES PHARES (curated) — desktop only. */}
        {!isMobile && curatedCities.length > 0 && (
          <section style={{ padding: isMobile ? "24px 0 16px" : "56px 0 24px" }}>
            <div style={{ marginBottom: 24 }}>
              <span className="gdx-eyebrow" style={{ color: "var(--g-coral)" }}>
                {t("continent_page_cities_eyebrow", { defaultValue: "VILLES PHARES" })}
              </span>
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: isMobile ? 24 : 32,
                  fontWeight: 800,
                  color: "var(--g-burgundy)",
                  marginTop: 6,
                }}
              >
                {t("continent_page_cities_title", {
                  defaultValue: "Les destinations les plus réservées",
                })}
              </h2>
            </div>
            <div
              style={{
                display: "grid",
                // Hard 6-up: one row max per 07-desktop-europe.jsx (6 cols).
                gridTemplateColumns: "repeat(6, 1fr)",
                gap: 16,
              }}
            >
              {curatedCities.slice(0, 6).map((city) => (
                <CityTile
                  key={city.slug}
                  city={city}
                  href={`/${lang}/${currency}/city/${city.slug}`}
                  t={t}
                  localeFormatter={localeFormatter}
                />
              ))}
            </div>
          </section>
        )}

        {/* WHY GUYDEEZ — desktop only. */}
        {!isMobile && (
        <section style={{ padding: "56px 0 0" }}>
          <div
            style={{
              background: "white",
              borderRadius: 16,
              border: "1px solid var(--g-cream-deep)",
              padding: "40px 32px",
              textAlign: "center",
            }}
          >
            <span className="gdx-eyebrow" style={{ color: "var(--g-coral)" }}>
              {t("continent_page_why_eyebrow", {
                continent: continentLabel,
                defaultValue: `POURQUOI GUYDEEZ EN ${continentLabel.toUpperCase()}`,
              })}
            </span>
            <h2
              style={{
                fontFamily: "var(--g-font-display)",
                fontSize: 24,
                fontWeight: 700,
                color: "var(--g-burgundy)",
                margin: "12px auto",
                maxWidth: 720,
              }}
            >
              {t("continent_page_why_title", {
                defaultValue:
                  "Pas de file d'attente, pas de surprise — juste un vrai local",
              })}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "var(--g-ink-500)",
                maxWidth: 680,
                margin: "0 auto",
                lineHeight: 1.55,
              }}
            >
              {t("continent_page_why_subtitle", {
                defaultValue:
                  "Guides francophones triés sur le volet, nés ou installés dans la ville où ils opèrent. Tarif final affiché, billets coupe-file inclus dans la plupart des tours.",
              })}
            </p>
          </div>
        </section>
        )}

        {/* CONTACT REASSURANCE — desktop: card with margins; mobile: full-bleed
            burgundy band like MobileServiceCTA (20-mobile-shared.jsx). */}
        <section
          style={{
            padding: isMobile ? "24px 0 0" : "32px 0 56px",
            // Escape the inner wrapper's 14px gutter on mobile so the band
            // extends edge-to-edge.
            marginLeft: isMobile ? -14 : 0,
            marginRight: isMobile ? -14 : 0,
          }}
        >
          <div
            style={{
              background: "var(--g-burgundy)",
              color: "white",
              borderRadius: isMobile ? 0 : 16,
              padding: isMobile ? "28px 18px" : "32px",
              display: isMobile ? "block" : "grid",
              gridTemplateColumns: isMobile
                ? undefined
                : "repeat(auto-fit, minmax(min(280px, 100%), 1fr))",
              gap: isMobile ? 0 : 32,
              alignItems: isMobile ? undefined : "center",
            }}
          >
            <div>
              <span
                className="gdx-eyebrow"
                style={{ color: "var(--g-coral-soft)" }}
              >
                {t("continent_page_hesitant_eyebrow", { defaultValue: "VOUS HÉSITEZ ?" })}
              </span>
              <h2
                style={{
                  fontFamily: "var(--g-font-display)",
                  fontSize: isMobile ? 20 : 24,
                  fontWeight: 800,
                  marginTop: 8,
                  marginBottom: 8,
                  lineHeight: 1.2,
                }}
              >
                {t("continent_page_hesitant_title", {
                  defaultValue:
                    "Discutez avec un conseiller — réponse en quelques minutes",
                })}
              </h2>
              <p
                style={{
                  fontSize: isMobile ? 12.5 : 13,
                  color: "rgba(255,255,255,.85)",
                  lineHeight: 1.55,
                  marginBottom: isMobile ? 18 : 0,
                }}
              >
                {t("continent_page_hesitant_subtitle", {
                  defaultValue:
                    "Posez votre question par chat, on vous aide à choisir le bon guide.",
                })}
              </p>
            </div>
            <Link
              href={`/${lang}/${currency}/contact` as any}
              style={{
                background: "var(--g-coral)",
                color: "white",
                fontWeight: 700,
                padding: isMobile ? "0 18px" : "14px 24px",
                height: isMobile ? 46 : "auto",
                borderRadius: 999,
                textDecoration: "none",
                fontSize: isMobile ? 13.5 : 14,
                display: isMobile ? "flex" : "inline-flex",
                width: isMobile ? "100%" : undefined,
                justifyContent: "center",
                alignItems: "center",
                gap: 8,
                whiteSpace: "nowrap",
              }}
            >
              💬 {t("continent_page_hesitant_cta", { defaultValue: "Démarrer la conversation" })}
            </Link>
          </div>
        </section>

        </div>
      </main>

      <SectionNewsletter />
    </>
  );
};

/* ---------- subcomponents ---------- */

const Stat: FC<{ value: string; label: string }> = ({ value, label }) => (
  <div>
    <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, fontFamily: "var(--g-font-display)" }}>
      {value}
    </div>
    <div
      style={{
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "rgba(255,255,255,.7)",
        marginTop: 6,
      }}
    >
      {label}
    </div>
  </div>
);

const TrustItem: FC<{ icon: string; label: React.ReactNode; accent?: boolean }> = ({
  icon,
  label,
  accent,
}) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 13,
      color: accent ? "var(--g-burgundy)" : "var(--g-ink-700)",
    }}
  >
    <span
      style={{
        color: accent ? "var(--g-coral)" : "var(--g-green-deep)",
        fontWeight: 700,
      }}
    >
      {icon}
    </span>
    <span>{label}</span>
  </span>
);

const CountryHeroCard: FC<{
  country: Country;
  badge: string | null;
  href: string;
  t: (key: string, opts?: any) => string;
  localeFormatter: Intl.NumberFormat;
}> = ({ country, badge, href, t, localeFormatter }) => {
  const [hovered, setHovered] = useState(false);
  const name = capitalize(country.nameTranslated) || country.name;
  // Badge enum → localized label. The 3 known enums map to existing i18n
  // keys; any unknown value (or null) renders no ribbon.
  const badgeLabel =
    badge === "most_booked"
      ? t("continent_page_badge_most", { defaultValue: "LE PLUS RÉSERVÉ" })
      : badge === "editor_choice"
        ? t("continent_page_badge_heart", { defaultValue: "COUP DE CŒUR" })
        : badge === "trending"
          ? t("continent_page_badge_trend", { defaultValue: "TOP TENDANCE" })
          : null;

  return (
    <Link
      href={href as any}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "block",
        width: "100%",
        minWidth: 0,
        height: 440,
        borderRadius: 16,
        overflow: "hidden",
        backgroundColor: "#4a6f5a",
        textDecoration: "none",
        color: "white",
        // Hover animation: subtle lift + deeper shadow. The background
        // image itself zooms via the child <div> below so the gradient,
        // badge and bottom block stay sharp while the photo breathes.
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 20px 40px -16px rgba(20,15,30,0.45)"
          : "0 6px 18px -10px rgba(20,15,30,0.25)",
        transition: "transform 320ms ease, box-shadow 320ms ease",
      }}
    >
      {/* Image layer — separated from the Link background so we can scale
          it on hover without distorting the overlay / badge / text. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: country.mainImage
            ? `url("${encodeURI(country.mainImage)}")`
            : undefined,
          backgroundColor: "#4a6f5a",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: hovered ? "scale(1.06)" : "scale(1)",
          transition: "transform 600ms cubic-bezier(.22,.61,.36,1)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(20,15,30,.05) 35%, rgba(20,15,30,.88) 100%)",
        }}
      />
      {badgeLabel && (
        <span
          style={{
            position: "absolute",
            top: 16,
            left: 16,
            background: "var(--g-coral)",
            color: "white",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.08em",
            padding: "6px 12px",
            borderRadius: 999,
          }}
        >
          {badgeLabel}
        </span>
      )}
      <div
        style={{
          position: "absolute",
          left: 22,
          right: 22,
          bottom: 22,
        }}
      >
        <div
          style={{
            // Bumped from 32 → 44 to match the mock's display hierarchy.
            fontSize: 44,
            fontWeight: 800,
            fontFamily: "var(--g-font-display)",
            letterSpacing: "-.015em",
            lineHeight: 1.05,
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: 13,
            color: "rgba(255,255,255,.85)",
            marginTop: 10,
            marginBottom: 22,
          }}
        >
          {[country.numCities, country.numTours]
            .map((v, i) =>
              v
                ? `${localeFormatter.format(v)} ${
                    i === 0
                      ? t("continents_card_countries_villes", { defaultValue: "villes" })
                      : t("continent_page_stat_tours", { defaultValue: "tours" })
                  }`
                : null
            )
            .filter(Boolean)
            .join(" · ")}
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 14px",
            background: "white",
            color: "var(--g-burgundy)",
            borderRadius: 999,
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          {t("continent_page_discover", { defaultValue: "Découvrir" })} →
        </span>
      </div>
    </Link>
  );
};

const CountryTile: FC<{
  country: Country;
  href: string;
  t: (key: string, opts?: any) => string;
  localeFormatter: Intl.NumberFormat;
  isMobile?: boolean;
  badge?: string | null;
}> = ({ country, href, t, localeFormatter, isMobile, badge }) => {
  const [hovered, setHovered] = useState(false);
  const name = capitalize(country.nameTranslated) || country.name;
  const counts = [country.numCities, country.numTours]
    .map((v, i) =>
      v
        ? `${localeFormatter.format(v)} ${
            i === 0
              ? t("continent_page_stat_cities", { defaultValue: "villes" })
              : t("continent_page_stat_tours", { defaultValue: "tours" })
          }`
        : null,
    )
    .filter(Boolean)
    .join(" · ");

  // Mobile: full-image card with overlaid name + counts + featured badge
  // (per 23-mobile-countries.jsx). No body strip below the image.
  if (isMobile) {
    const isFeatured = badge === "editor_choice" || badge === "most_booked" || badge === "trending";
    return (
      <Link
        href={href as any}
        style={{
          position: "relative",
          display: "block",
          borderRadius: 12,
          overflow: "hidden",
          background: "#4a6f5a",
          textDecoration: "none",
          color: "white",
          height: 150,
          backgroundImage: country.mainImage
            ? `url("${encodeURI(country.mainImage)}")`
            : undefined,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, transparent 40%, rgba(0,0,0,.7))",
          }}
        />
        {isFeatured && (
          <div
            style={{
              position: "absolute",
              top: 8,
              left: 8,
              padding: "3px 8px",
              background: "rgba(255,255,255,.95)",
              borderRadius: 999,
              fontSize: 9,
              fontWeight: 700,
              color: "var(--g-burgundy)",
            }}
          >
            ★ {t("continent_page_badge_heart", { defaultValue: "Coup de cœur" })}
          </div>
        )}
        <div
          style={{
            position: "absolute",
            bottom: 8,
            left: 10,
            right: 10,
            color: "white",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.15 }}>
            {name}
          </div>
          {counts && (
            <div style={{ fontSize: 10, opacity: 0.9, marginTop: 2 }}>
              {counts}
            </div>
          )}
        </div>
      </Link>
    );
  }

  return (
    <Link
      href={href as any}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        borderRadius: 12,
        overflow: "hidden",
        background: "white",
        border: "1px solid var(--g-cream-deep)",
        textDecoration: "none",
        color: "var(--g-burgundy)",
        // Subtle hover: lift + shadow boost. The thumbnail underneath
        // zooms ~3% (less aggressive than the hero card because these
        // tiles are denser on the page).
        transform: hovered ? "translateY(-3px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 16px 28px -16px rgba(20,15,30,0.35)"
          : "0 2px 6px -4px rgba(20,15,30,0.18)",
        transition: "transform 260ms ease, box-shadow 260ms ease",
      }}
    >
      <div
        style={{
          position: "relative",
          aspectRatio: "16 / 10",
          backgroundImage: country.mainImage
            ? `url("${encodeURI(country.mainImage)}")`
            : undefined,
          backgroundColor: "#4a6f5a",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: hovered ? "scale(1.035)" : "scale(1)",
          transition: "transform 500ms cubic-bezier(.22,.61,.36,1)",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, transparent 40%, rgba(20,15,30,.85) 100%)",
          }}
        />
        <div
          style={{
            position: "absolute",
            left: 14,
            bottom: 12,
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: 18,
              fontWeight: 800,
              fontFamily: "var(--g-font-display)",
              lineHeight: 1.1,
            }}
          >
            {name}
          </div>
        </div>
      </div>
      <div
        style={{
          padding: "10px 14px",
          fontSize: 12,
          color: "var(--g-ink-500)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>{counts}</span>
        <span style={{ color: "var(--g-coral)", fontWeight: 700 }}>→</span>
      </div>
    </Link>
  );
};

const CityTile: FC<{
  city: FeaturedCity;
  href: string;
  t: (key: string, opts?: any) => string;
  localeFormatter: Intl.NumberFormat;
}> = ({ city, href, t, localeFormatter }) => {
  const [hovered, setHovered] = useState(false);
  const title = city.entry?.data?.[0]?.title;
  const name = capitalize(
    title && title !== city.slug ? title : city.nameTranslated || city.name
  );
  const image =
    city.entry?.data?.[0]?.mainImage || city.mainImage || "";
  return (
    <Link
      href={href as any}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: "relative",
        display: "block",
        borderRadius: 10,
        overflow: "hidden",
        aspectRatio: "4 / 5",
        backgroundColor: "#4a6f5a",
        textDecoration: "none",
        color: "white",
        transform: hovered ? "translateY(-2px)" : "translateY(0)",
        boxShadow: hovered
          ? "0 14px 24px -14px rgba(20,15,30,0.35)"
          : "0 2px 6px -4px rgba(20,15,30,0.18)",
        transition: "transform 240ms ease, box-shadow 240ms ease",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: image ? `url("${encodeURI(image)}")` : undefined,
          backgroundColor: "#4a6f5a",
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: hovered ? "scale(1.05)" : "scale(1)",
          transition: "transform 500ms cubic-bezier(.22,.61,.36,1)",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, transparent 45%, rgba(20,15,30,.85) 100%)",
        }}
      />
      <div style={{ position: "absolute", left: 10, right: 10, bottom: 10 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            fontFamily: "var(--g-font-display)",
            lineHeight: 1.1,
          }}
        >
          {name}
        </div>
        {(() => {
          const tours = city.totalTours ?? city.tourCount ?? 0;
          if (!tours) return null;
          return (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.85)", marginTop: 4 }}>
              {t("continent_page_stat_tours_count", {
                count: tours,
                defaultValue: `${localeFormatter.format(tours)} tours`,
              })}
            </div>
          );
        })()}
      </div>
    </Link>
  );
};

const capitalize = (s?: string) => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-")
    )
    .join(" ");
};

export default ContinentPage;
export { ContinentPage };
